import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiDownload,
  FiEdit3,
  FiExternalLink,
  FiFolder,
  FiPaperclip,
  FiTrash2,
  FiUploadCloud,
} from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../AuthContext';
import resolveDownloadPath from '../utils/downloads';
import FilePreviewModal from './FilePreviewModal';

const toFileObject = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && value.__removed === true) return null;
  if (typeof value === 'string') {
    return { url: value, name: 'Baixar arquivo', metadata: null };
  }
  return value;
};

const managementTokenFrom = (value) => {
  if (!value || typeof value !== 'object') return '';
  const direct = typeof value.managementToken === 'string' ? value.managementToken.trim() : '';
  if (direct) return direct;
  const hidden = value.metadata?.__managementToken;
  return typeof hidden === 'string' ? hidden.trim() : '';
};

const resolveExternalUploadEndpoint = () => {
  if (typeof window === 'undefined') return null;
  const pathname = String(window.location?.pathname || '');
  const segments = pathname.split('/').filter(Boolean);
  const token = segments.length ? decodeURIComponent(segments[segments.length - 1]) : '';
  if (!token) return null;
  const kind = pathname.includes('/tarefas-externas/') ? 'step-tasks' : 'forms';
  return `/upload/external/${kind}/${encodeURIComponent(token)}`;
};

const contextFrom = (value, previous = {}) => {
  const metadata = value?.metadata && typeof value.metadata === 'object'
    ? value.metadata
    : {};
  return {
    endpoint: previous.endpoint || resolveExternalUploadEndpoint(),
    fieldId: String(metadata.fieldId || previous.fieldId || '').trim(),
    columnId: String(metadata.columnId || previous.columnId || '').trim(),
    required:
      metadata.required === undefined
        ? Boolean(previous.required)
        : Boolean(metadata.required),
  };
};

const replaceObjectContents = (target, nextValue) => {
  if (!target || typeof target !== 'object' || Array.isArray(target)) return;
  Object.keys(target).forEach((key) => delete target[key]);
  try {
    delete target.__removed;
    delete target.toJSON;
  } catch {}
  Object.assign(target, nextValue);
};

const markObjectAsRemoved = (target) => {
  if (!target || typeof target !== 'object' || Array.isArray(target)) return;
  Object.keys(target).forEach((key) => delete target[key]);
  Object.defineProperty(target, '__removed', {
    value: true,
    enumerable: false,
    configurable: true,
  });
  Object.defineProperty(target, 'toJSON', {
    value: () => null,
    enumerable: false,
    configurable: true,
  });
};

const buildUploadedFile = (data) => ({
  url: data?.url,
  downloadUrl: data?.downloadUrl,
  downloadPath: data?.downloadPath,
  relativePath: data?.relativePath,
  name: data?.name || 'Arquivo',
  metadata: data?.metadata || null,
});

export default function FileAttachment({ file, onOpenFileManager, instanceId, compact = false }) {
  const { token } = useAuth();
  const initialObject = toFileObject(file);
  const sourceObjectRef = useRef(file && typeof file === 'object' ? file : null);
  const editContextRef = useRef(contextFrom(initialObject));
  const fileInputRef = useRef(null);
  const [displayFile, setDisplayFile] = useState(initialObject);
  const [managementToken, setManagementToken] = useState(() => managementTokenFrom(initialObject));
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (file === sourceObjectRef.current) return;
    sourceObjectRef.current = file && typeof file === 'object' ? file : null;
    const next = toFileObject(file);
    editContextRef.current = contextFrom(next, editContextRef.current);
    setDisplayFile(next);
    setManagementToken(managementTokenFrom(next));
  }, [file]);

  const meta = useMemo(() => {
    const current = toFileObject(displayFile);
    if (!current) return null;
    return {
      url: current.url,
      downloadUrl: current.downloadUrl,
      downloadPath: current.downloadPath,
      name: current.name || 'Baixar arquivo',
      relativePath: current.relativePath,
      metadata: current.metadata || null,
    };
  }, [displayFile]);

  if (meta?.metadata) {
    editContextRef.current = contextFrom(meta, editContextRef.current);
  }

  const editableEndpoint = editContextRef.current.endpoint;
  const fieldId = editContextRef.current.fieldId;
  const required = editContextRef.current.required;
  const canEditExternalUpload = Boolean(
    editableEndpoint && managementToken && fieldId && meta?.relativePath,
  );

  const resolveFileRequestPath = () => {
    if (!meta) return null;
    const source = meta.downloadUrl || meta.downloadPath || meta.relativePath || meta.url;
    if (instanceId && source) {
      let pathValue = source;
      try {
        const parsed = new URL(source, window.location.origin);
        pathValue = parsed.searchParams.get('path') || parsed.pathname
          .replace(/^\/+(?:api\/)?uploads\//i, 'uploads/')
          .replace(/^\/+/, '');
      } catch {
        pathValue = String(source)
          .replace(/^\/+(?:api\/)?uploads\//i, 'uploads/')
          .replace(/^\/+/, '');
      }
      return `/processes/instances/${instanceId}/files/content?path=${encodeURIComponent(pathValue)}`;
    }
    if (meta.downloadUrl) return resolveDownloadPath(meta.downloadUrl);
    if (meta.downloadPath) return resolveDownloadPath(meta.downloadPath);
    return null;
  };

  const normalizeCandidate = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;

    let candidate = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    candidate = candidate
      .replace(/\/{2,}/g, '/')
      .replace(/^\/api(?:\/api)+(\/|$)/i, '/api$1')
      .replace(/^\/(?:api\/)+files(\/|$)/i, '/files$1')
      .replace(/^\/(?:api\/)+uploads(\/|$)/i, '/uploads$1');

    const baseURL = String(api?.defaults?.baseURL || '').trim();
    const baseHasApiPrefix = /\/api\/?$/i.test(baseURL);
    if (baseHasApiPrefix && /^\/api(\/|$)/i.test(candidate)) {
      candidate = candidate.replace(/^\/api(?=\/|$)/i, '') || '/';
    }
    return candidate;
  };

  const buildRequestCandidates = (primaryPath) => {
    if (!meta) return [];
    const candidates = [];
    const push = (value) => {
      const normalized = normalizeCandidate(value);
      if (normalized && !candidates.includes(normalized)) candidates.push(normalized);
    };

    [
      primaryPath,
      resolveDownloadPath(meta.downloadUrl || ''),
      resolveDownloadPath(meta.downloadPath || ''),
    ].forEach((source) => {
      push(source);
      if (typeof source !== 'string' || /^https?:\/\//i.test(source)) return;
      const [pathname = '', query = ''] = source.split('?');
      if (/^\/(?:api\/)?files$/i.test(pathname)) {
        push(`/files${query ? `?${query}` : ''}`);
      }
      if (query) {
        const params = new URLSearchParams(query);
        const rawPath = params.get('path');
        if (rawPath) push(`/uploads/${rawPath.replace(/^\/+/, '')}`);
      }
    });

    return candidates;
  };

  const fetchFileBlob = async (primaryPath, options = {}) => {
    const candidates = buildRequestCandidates(primaryPath);
    let lastError = null;
    for (const candidate of candidates) {
      try {
        return await api.get(candidate, {
          responseType: 'blob',
          signal: options.signal,
        });
      } catch (error) {
        lastError = error;
        const status = error?.response?.status;
        if (status && status !== 404) throw error;
      }
    }
    throw lastError || new Error('Arquivo não encontrado');
  };

  const loadPreviewFile = ({ signal } = {}) => {
    const requestPath = resolveFileRequestPath();
    if (!requestPath) {
      if (meta?.url) return fetch(meta.url, { signal }).then((response) => response.blob());
      return Promise.reject(new Error('Arquivo não encontrado.'));
    }
    return fetchFileBlob(requestPath, { signal });
  };

  const handleOpen = (event) => {
    event.preventDefault();
    if (!meta || (!resolveFileRequestPath() && !meta.url)) return;
    if (!token && resolveFileRequestPath()) {
      alert('Faça login para abrir este arquivo.');
      return;
    }
    setPreviewOpen(true);
  };

  const handleDownload = async (event) => {
    event.preventDefault();
    if (!meta || downloading) return;
    const requestPath = resolveFileRequestPath();
    if (!requestPath) {
      if (meta.url) window.open(meta.url, '_blank', 'noopener');
      return;
    }
    if (!token) {
      alert('Faça login para baixar este arquivo.');
      return;
    }

    setDownloading(true);
    try {
      const response = await fetchFileBlob(requestPath);
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = meta.name || 'arquivo';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Erro ao baixar arquivo', error);
      alert('Não foi possível baixar este arquivo.');
    } finally {
      setDownloading(false);
    }
  };

  const removeUploadedFile = async ({ quiet = false } = {}) => {
    if (!editableEndpoint || !managementToken || !meta?.relativePath) return false;
    try {
      await api.delete(editableEndpoint, {
        data: {
          relativePath: meta.relativePath,
          managementToken,
        },
      });
      return true;
    } catch (error) {
      if (!quiet) {
        alert(error?.response?.data?.error || 'Não foi possível excluir este arquivo.');
      }
      return false;
    }
  };

  const handleRemove = async () => {
    if (removing || editing) return;
    setRemoving(true);
    const removed = await removeUploadedFile();
    if (removed) {
      markObjectAsRemoved(sourceObjectRef.current);
      setDisplayFile(null);
      setManagementToken('');
      setPreviewOpen(false);
    }
    setRemoving(false);
  };

  const handleReplacement = async (selectedFile) => {
    if (!selectedFile || editing) return;
    const endpoint = editContextRef.current.endpoint || resolveExternalUploadEndpoint();
    const currentFieldId = editContextRef.current.fieldId;
    const currentColumnId = editContextRef.current.columnId;
    if (!endpoint || !currentFieldId) {
      alert('Não foi possível identificar o campo deste arquivo.');
      return;
    }

    const previousPath = meta?.relativePath || '';
    const previousToken = managementToken;
    setEditing(true);
    setUploadPercent(0);
    try {
      const data = new FormData();
      data.append('file', selectedFile);
      data.append('fieldId', currentFieldId);
      if (currentColumnId) data.append('columnId', currentColumnId);

      const response = await api.post(endpoint, data, {
        onUploadProgress: (event) => {
          const percent = event?.total
            ? Math.round((event.loaded * 100) / event.total)
            : 0;
          setUploadPercent(percent);
        },
      });
      const nextFile = buildUploadedFile(response.data);
      const nextToken =
        String(response.data?.managementToken || '').trim() ||
        managementTokenFrom(nextFile);
      if (!nextFile.relativePath || !nextToken) {
        throw new Error('O servidor não autorizou a edição do novo arquivo.');
      }

      editContextRef.current = contextFrom(nextFile, editContextRef.current);
      replaceObjectContents(sourceObjectRef.current, nextFile);
      setDisplayFile(nextFile);
      setManagementToken(nextToken);
      setUploadPercent(100);

      if (previousPath && previousToken) {
        try {
          await api.delete(endpoint, {
            data: {
              relativePath: previousPath,
              managementToken: previousToken,
            },
          });
        } catch (cleanupError) {
          console.error('O novo arquivo foi salvo, mas o anterior não pôde ser removido.', cleanupError);
        }
      }
    } catch (error) {
      alert(error?.response?.data?.error || error?.message || 'Não foi possível alterar este arquivo.');
    } finally {
      setEditing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const showFileManagerButton = typeof onOpenFileManager === 'function';
  const containerClassName = compact
    ? 'flex max-w-full min-w-0 flex-col gap-2 overflow-hidden rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs shadow-sm sm:flex-row sm:items-start sm:justify-between'
    : 'flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm';
  const detailsClassName = compact
    ? 'flex min-w-0 max-w-full flex-1 items-start space-x-2 overflow-hidden'
    : 'flex min-w-0 flex-1 items-center space-x-2';
  const actionsClassName = compact
    ? 'flex max-w-full shrink-0 flex-wrap items-center gap-1 overflow-hidden'
    : 'ml-3 flex flex-wrap items-center gap-2';
  const buttonClassName = compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1 text-xs';

  if (!meta && (!editableEndpoint || !fieldId)) {
    return <span className="text-sm text-slate-500">Sem arquivo</span>;
  }

  if (!meta) {
    return (
      <div className="space-y-2 rounded-lg border border-dashed border-blue-200 bg-blue-50/60 p-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-blue-500 bg-blue-500 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-600">
          <FiUploadCloud />
          <span>Selecionar novo arquivo</span>
          <input
            ref={fileInputRef}
            type="file"
            aria-label="Selecionar novo arquivo"
            required={required}
            className="sr-only"
            disabled={editing}
            onChange={(event) => handleReplacement(event.target.files?.[0])}
          />
        </label>
        {editing && (
          <p className="text-xs text-blue-600">
            Enviando novo arquivo... {uploadPercent}%
          </p>
        )}
      </div>
    );
  }

  const requestHint = (() => {
    const metadata = meta.metadata;
    if (!metadata) return '';
    const parts = [];
    if (metadata.requestId) parts.push(`Solicitação ${metadata.requestId}`);
    if (metadata.requestProtocol) parts.push(`Protocolo ${metadata.requestProtocol}`);
    if (metadata.requesterName) parts.push(metadata.requesterName);
    if (metadata.requesterSector) parts.push(metadata.requesterSector);
    if (metadata.insertedAt) {
      const when = new Date(metadata.insertedAt);
      if (!Number.isNaN(when.getTime())) parts.push(when.toLocaleString());
    }
    return parts.join(' · ');
  })();

  return (
    <>
      <div className={containerClassName}>
        <div className={detailsClassName}>
          <FiPaperclip className="shrink-0 text-slate-500" />
          <div className="flex min-w-0 max-w-full flex-col overflow-hidden">
            <span className="max-w-full truncate font-medium text-slate-700" title={meta.name || 'Arquivo'}>
              {meta.name || 'Arquivo'}
            </span>
            {meta.relativePath && (
              <span className="max-w-full truncate text-xs text-slate-400" title={meta.relativePath}>
                {meta.relativePath}
              </span>
            )}
            {requestHint && (
              <span className="max-w-full truncate text-xs text-slate-400" title={requestHint}>
                {requestHint}
              </span>
            )}
            {editing && (
              <span className="text-xs font-medium text-blue-600">
                Enviando novo arquivo... {uploadPercent}%
              </span>
            )}
          </div>
        </div>
        <div className={actionsClassName}>
          <button
            type="button"
            onClick={handleOpen}
            disabled={previewOpen || editing || removing}
            className={`inline-flex items-center space-x-1 rounded-md border border-blue-500 bg-blue-500 font-semibold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
          >
            <FiExternalLink />
            <span>Visualizar</span>
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || editing || removing}
            className={`inline-flex items-center space-x-1 rounded-md border border-blue-500 bg-blue-500 font-semibold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
          >
            <FiDownload />
            <span>{downloading ? 'Baixando...' : 'Baixar'}</span>
          </button>
          {canEditExternalUpload && (
            <>
              <button
                type="button"
                aria-label="Alterar arquivo"
                onClick={() => fileInputRef.current?.click()}
                disabled={editing || removing}
                className={`inline-flex items-center space-x-1 rounded-md border border-amber-500 bg-white font-semibold text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
              >
                <FiEdit3 />
                <span>Alterar</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                aria-label="Selecionar novo arquivo"
                className="sr-only"
                disabled={editing || removing}
                onChange={(event) => handleReplacement(event.target.files?.[0])}
              />
              <button
                type="button"
                aria-label="Excluir arquivo"
                onClick={handleRemove}
                disabled={editing || removing}
                className={`inline-flex items-center space-x-1 rounded-md border border-red-500 bg-white font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
              >
                <FiTrash2 />
                <span>{removing ? 'Excluindo...' : 'Excluir'}</span>
              </button>
            </>
          )}
          {showFileManagerButton && (
            <button
              type="button"
              onClick={() => onOpenFileManager(file)}
              className={`inline-flex items-center space-x-1 rounded-md border border-blue-500 bg-blue-500 font-semibold text-white transition hover:bg-blue-600 ${buttonClassName}`}
            >
              <FiFolder />
              <span>Gerenciador</span>
            </button>
          )}
        </div>
      </div>
      <FilePreviewModal
        isOpen={previewOpen}
        fileName={meta.name || 'arquivo'}
        loadFile={loadPreviewFile}
        onClose={() => setPreviewOpen(false)}
        onDownload={(event) => handleDownload(event || { preventDefault: () => {} })}
      />
    </>
  );
}
